import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, Event as NavigationEvent, NavigationStart } from '@angular/router';
import { takeUntil } from 'rxjs';
import { AuthService } from 'src/app/service/auth.service';
import { Destroy } from 'src/app/service/destroy';
import { StorageService } from 'src/app/service/storage.service';
import { TranslationService } from 'src/app/service/translation.service';
import { LanguageService } from 'src/app/service/language.service';
import { CommonService } from 'src/app/service/common.service';
import { navs } from 'src/app/utils/navigations';

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.css'],
    viewProviders: [Destroy],
    standalone: false
})
export class SideNavComponent implements OnInit {

  navItems: any[] = [];
  userData: any;
  currentUrl: string = '';
  sidebarOpen: boolean = true;
  
  constructor(private authService: AuthService, private storage: StorageService, public destroy$: Destroy, private router: Router, private route: ActivatedRoute, private translationService: TranslationService, public languageService: LanguageService, private commonService: CommonService){
   this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: NavigationEvent) => {
    if(event instanceof NavigationStart){
      this.currentUrl = event.url;
      // Close sidebar on mobile when navigating
      if (this.commonService.isMobile$.value) {
        this.commonService.sidebarOpen$.next(false);
      }
    }
   })
  }

  ngOnInit(): void {
    // Load sidebar immediately
    this.loadSideBar();

    // Subscribe to sidebar open/close state
    this.commonService.sidebarOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
      this.sidebarOpen = isOpen;
    });

    // Update nav labels when language changes or when translations are ready
    this.translationService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.translateNavItems());

    this.translationService.translationsReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ready) => {
        if (ready) this.translateNavItems();
      });
  }

  getUserData() {
    this.storage.getItem('local', 'authData').pipe(takeUntil(this.destroy$)).subscribe((userData: any) => {
      this.userData = userData;
    })
  }

  private loadSideBar(): void {
    this.authService.getSideBar().pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      // parse the response into navItems
      
      // Handle different response structures
      if (Array.isArray(response)) {
        this.navItems = response;
      } else if (response?.data && Array.isArray(response.data)) {
        this.navItems = response.data;
      } else if (response?.navs && Array.isArray(response.navs)) {
        this.navItems = response.navs;
      } else if (response) {
        // If response itself is the nav array or object, try to extract it
        this.navItems = typeof response === 'object' ? Object.values(response).filter((item: any) => item?.routerLink) : [];
      } else {
        this.navItems = [];
      }
      
      // If navItems is still empty, use fallback
      if (!this.navItems || this.navItems.length === 0) {
        this.navItems = this.getFallbackNavItems();
      }

      this.translateNavItems();
      this.getUserData();
    },
    (error) => {
      this.navItems = this.getFallbackNavItems();
      this.translateNavItems();
    });
  }

  private getFallbackNavItems(): any[] {
    // Fallback navigation items if API fails or returns empty
    return [
      {
        id: 1,
        name: 'Dashboard',
        routerLink: '/dashboard',
        iconClass: 'sidebar-dashboard',
      },
      {
        id: 11,
        name: 'All Orders',
        routerLink: '/orders',
        iconClass: 'sidebar-profile',
      },
      {
        id: 12,
        name: 'Menu',
        routerLink: '/menu',
        iconClass: 'sidebar-profile',
      },
      {
        id: 13,
        name: 'Products',
        routerLink: '/products',
        iconClass: 'sidebar-profile',
      },
      {
        id: 14,
        name: 'Ratings & Reviews',
        routerLink: '/ratings-reviews',
        iconClass: 'sidebar-profile',
      },
      {
        id: 15,
        name: 'Store Profile',
        routerLink: '/store-profile',
        iconClass: 'sidebar-profile',
      }
    ];
  }

  private translateNavItems(): void {
    // Only translate if navItems is an array with items
    if (!Array.isArray(this.navItems) || this.navItems.length === 0) return;

    // Create a translation key mapping for nav item names
    const translationMap: { [key: string]: string } = {
      'Dashboard': 'navigation.dashboard',
      'Quotations': 'navigation.quotations',
      'Chats': 'navigation.chat',
      'My Earnings': 'navigation.earnings',
      'My Reviews': 'navigation.reviews',
      'Job History': 'navigation.jobHistory',
      'Cancelled Jobs': 'navigation.cancelledJobs',
      'My Documents': 'navigation.documents',
      'Employee List': 'navigation.employees',
      'My Profile': 'navigation.myProfile',
      'All Orders': 'navigation.allOrders',
      'Menu': 'navigation.menu',
      'Products': 'navigation.products',
      'Ratings & Reviews': 'navigation.ratingsReviews',
      'Store Profile': 'navigation.storeProfile',
      'FAQs': 'navigation.faqs',
      'Legal': 'navigation.legal'
    };
    // Translate nav items - add displayName to each item
    this.navItems.forEach((item: any) => {
      const key = translationMap[item.name];
      item.displayName = key ? this.translationService.instant(key, item.name) : item.name;
    });
  }

  routeTo(route: string){
    this.router.navigate([`${route}`]);
  }

  get isArabic(): boolean {
    return this.languageService.current === 'ar';
  }

}
